/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

export type StreamStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'forbidden' | 'stopped' | 'unavailable';

export type TabToWorker =
  | { type: 'hello'; scopeOk: boolean }
  | { type: 'subscribe'; serials: string[] }
  | { type: 'unsubscribe'; serials: string[] }
  /** The keys this tab wants on a channel (e.g. the instant KPIs); an empty list leaves the channel. */
  | { type: 'channel'; channel: string; keys: string[] }
  | { type: 'pong' }
  | { type: 'logout' }
  | { type: 'bye' };

export type WorkerToTab =
  | { type: 'event'; eventType: string; data: unknown }
  | { type: 'state'; status: StreamStatus }
  | { type: 'channel-state'; channel: string; status: StreamStatus }
  /** The tab's keys on a channel that the stream could not hold; they get no events until room frees up. */
  | { type: 'channel-dropped'; channel: string; keys: string[] }
  | { type: 'reconnected' }
  | { type: 'login-required' }
  | { type: 'ping' };

export interface StreamPort<Out, In> {
  postMessage(message: Out): void;
  onmessage: ((event: { data: In }) => void) | null;
}

export type TabPort = StreamPort<TabToWorker, WorkerToTab>;
export type WorkerPort = StreamPort<WorkerToTab, TabToWorker>;

export const METER_READING_EVENT = 'meter.reading';
/**
 * A meter's latest value of every register, merged across the operations that each carry only some of them:
 * `{"<SERIAL>": reading | null}`. The backend sends one per meter when the stream opens; null means no reading.
 */
export const METER_LATEST_EVENT = 'meter.latest';
/**
 * The instant KPI stream: a serial-less channel with its own ticket and connection, opened with the
 * union of the KPIs the tabs ask for, and only while at least one asks for one.
 */
export const INSTANT_KPI_CHANNEL = 'instant-kpis';
const INSTANT_KPI_EVENT_PREFIX = 'instant-kpi.';

export function instantKpiEvent(kpi: string): string {
  return `${INSTANT_KPI_EVENT_PREFIX}${kpi}`;
}

/** The KPI id of an instant KPI event type, or null for any other event. */
export function instantKpiOf(eventType: string): string | null {
  return eventType.startsWith(INSTANT_KPI_EVENT_PREFIX) ? eventType.slice(INSTANT_KPI_EVENT_PREFIX.length) : null;
}
/**
 * The live last-hour figures (spec 011): a serial-less channel whose keys are `incomers` and `meter:<SERIAL>`.
 * The keys select which figures are delivered; computation runs whether or not anyone is watching.
 */
export const WINDOW_METRICS_CHANNEL = 'window-metrics';
export const WINDOW_METRICS_METER_EVENT = 'window-metrics.meter';
export const WINDOW_METRICS_INCOMERS_EVENT = 'window-metrics.incomers';
export const INCOMERS_WINDOW_KEY = 'incomers';

const METER_WINDOW_KEY_PREFIX = 'meter:';
/** The backend rejects a window-metrics stream holding more `meter:` keys than this (`max-meter-keys`). */
export const MAX_WINDOW_METER_KEYS = 10;

export function meterWindowKey(serial: string): string {
  return `${METER_WINDOW_KEY_PREFIX}${serial.toUpperCase()}`;
}

/** Keeps every non-meter key and the first {@link MAX_WINDOW_METER_KEYS} meter keys, so the stream is never rejected. */
export function limitWindowKeys(keys: string[]): string[] {
  let meters = 0;
  return keys.filter((key) => !key.startsWith(METER_WINDOW_KEY_PREFIX) || ++meters <= MAX_WINDOW_METER_KEYS);
}

/** The key of a window-metrics event: its payload's `windowKey`. Null for any other event. */
export function windowKeyOf(eventType: string, data: unknown): string | null {
  if (eventType !== WINDOW_METRICS_METER_EVENT && eventType !== WINDOW_METRICS_INCOMERS_EVENT) return null;
  const key = data && typeof data === 'object' ? (data as { windowKey?: unknown }).windowKey : undefined;
  return typeof key === 'string' ? key : null;
}

/** Each stream has its own scope: holding one never grants another. */
export const METER_READINGS_SCOPE = 'stream:meter-readings:read';
export const INSTANT_KPIS_SCOPE = 'stream:instant-kpis:read';
export const WINDOW_METRICS_SCOPE = 'stream:window-metrics:read';
/** The scope each channel's stream requires. */
export const CHANNEL_SCOPES: Readonly<Record<string, string>> = {
  [INSTANT_KPI_CHANNEL]: INSTANT_KPIS_SCOPE,
  [WINDOW_METRICS_CHANNEL]: WINDOW_METRICS_SCOPE,
};
export const MAX_STREAM_SERIALS = 50;
